import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Worker { id: string; name: string; role: string; }

interface Props {
  onClose: () => void;
  workerId?: string;
  certType?: string;
}

export function AddCertModal({ onClose, workerId: initialWorkerId, certType: initialCertType }: Props) {
  const qc = useQueryClient();
  const [workerId, setWorkerId] = useState(initialWorkerId ?? '');
  const [type, setType] = useState(initialCertType ?? '');
  const [expiry, setExpiry] = useState('');
  const [status, setStatus] = useState<'valid' | 'expiring' | 'expired'>('valid');
  const [docRef, setDocRef] = useState('');
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [docName, setDocName] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });

  function autoStatusFromExpiry(d: string) {
    if (!d) return;
    const dt = new Date(d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = Math.floor((dt.getTime() - today.getTime()) / 86400000);
    if (days < 0) setStatus('expired');
    else if (days < 90) setStatus('expiring');
    else setStatus('valid');
  }

  async function handleFile(file: File) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<{ url: string }>('/uploads/file', fd);
      setDocUrl(res.data.url);
      setDocName(file.name);
      toast.success('Document uploaded', file.name);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function clearDoc() { setDocUrl(null); setDocName(''); }

  const save = useMutation({
    mutationFn: async () => api.post(`/workers/${workerId}/certs`, {
      type: type.trim(),
      expiry: expiry || null,
      status,
      docRef: docRef.trim() || null,
      docUrl: docUrl ?? null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['training', 'matrix'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
      toast.success('Certificate recorded', type);
      onClose();
    },
    onError: (e: any) => toast.error('Save failed', e?.response?.data?.message ?? 'Check fields and try again'),
  });

  const lockedType = !!initialCertType;

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">◫ Record Certificate</div>
        <div className="modal-sub">Add or renew a training certificate for a worker</div>
        <div className="form-row">
          <label className="form-label">Worker</label>
          <select className="form-select" id="acert-worker" value={workerId} onChange={(e) => setWorkerId(e.target.value)} disabled={!!initialWorkerId}>
            <option value="">— Select worker —</option>
            {workers.map((w) => <option key={w.id} value={w.id}>{w.name}{w.role ? ` — ${w.role}` : ''}</option>)}
          </select>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Certificate Type {lockedType ? <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(from selected cell)</span> : null}</label>
            {lockedType ? (
              <div className="form-input" id="acert-type" style={{ background: 'var(--surface)', color: 'var(--accent)', fontWeight: 600, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--accent)' }}>{type}</div>
            ) : (
              <input type="text" className="form-input" id="acert-type" placeholder="e.g. SMSTS, First Aid, CSCS Gold" value={type} onChange={(e) => setType(e.target.value)} />
            )}
          </div>
          <div className="form-row">
            <label className="form-label">Expiry Date</label>
            <input type="date" className="form-input" id="acert-expiry" value={expiry} onChange={(e) => { setExpiry(e.target.value); autoStatusFromExpiry(e.target.value); }} />
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">Status</label>
          <select className="form-select" id="acert-status" value={status} onChange={(e) => setStatus(e.target.value as 'valid' | 'expiring' | 'expired')}>
            <option value="valid">✓ Valid</option>
            <option value="expiring">⏱ Expiring Soon</option>
            <option value="expired">✗ Expired</option>
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Document / Certificate Reference</label>
          <input type="text" className="form-input" id="acert-docref" placeholder="e.g. SMSTS-2024-001.pdf" value={docRef} onChange={(e) => setDocRef(e.target.value)} />
        </div>
        <div className="form-row">
          <label className="form-label">Certificate Document — drag &amp; drop or click to attach</label>
          <div
            className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
            id="acert-dropzone"
            style={{ padding: 14, borderRadius: 8, border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`, background: dragOver ? 'rgba(232,164,39,.08)' : 'var(--surface)', textAlign: 'center', cursor: 'pointer', transition: 'all .15s' }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <div style={{ fontSize: 20, marginBottom: 5 }}>📂</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>{uploading ? 'Uploading…' : 'Drop PDF, JPG or PNG here'}</div>
            <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>or click to browse files</div>
            <input
              type="file"
              id="acert-file-input"
              ref={fileInputRef}
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </div>
          {docUrl ? (
            <div id="acert-docbadge" style={{ marginTop: 7, display: 'flex', alignItems: 'center', gap: 6, padding: '5px 9px', background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 6, fontSize: 11, color: '#22c55e' }}>
              <span>📎</span>
              <a href={docUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit', flex: 1, textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{docName || docUrl}</a>
              <button onClick={clearDoc} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 12 }} title="Remove">✕</button>
            </div>
          ) : null}
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !workerId || !type.trim()}>
            {save.isPending ? 'Saving…' : 'Save Certificate'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
